import React, { useState } from "react";
import { Box, Button, ClickAwayListener, Modal, Popper, TextField, Typography, InputAdornment } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import theme from "../../../../../Layouts/theme";
import SearchIcon from "../../../../../../../app/assets/images/icons/search.svg";
import { FormattedMessage, useIntl } from "react-intl";
import ManageBoardPanel from "./ManageBoardPanel";
import ExternalCandidateModal from "./ExternalCandidateModal";
import trackEvent from "bundles/common/utils/trackEvent";

const HeaderRowKanban = ({ stepsCount, query, onQueryChange, onAddCandidate, columns, cardsByColumnId, setColumns, setCardsByColumnId, onBoardMutated }) => {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const intl = useIntl();
  const [manageOpen, setManageOpen] = useState(false);
  const [manageAnchorEl, setManageAnchorEl] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  return (
    <Box sx={{ position: "relative", display: isDesktop ? "flex" : "block", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: isDesktop ? "24px" : "20px !important", color: theme.palette.secondary.black, fontWeight: "600 !important", fontFamily: "Segoe UI Semibold, sans-serif" }}>
          <FormattedMessage id="recruiter.kanban.board" />
        </Typography>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            padding: "0px 8px",
            borderRadius: "6px",
            backgroundColor: "#F4FFC1",
            color: theme.palette.text.main,
            fontWeight: 700,
            fontFamily: "Segoe UI Bold, sans-serif",
            fontSize: isDesktop ? "24px" : "20px",
            whiteSpace: "nowrap",
          }}
        >
          {stepsCount}
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          gap: "12px",
          flexDirection: isDesktop ? "row" : "column",
          alignItems: isDesktop ? "center" : "stretch",
          justifyContent: "space-between",
          margin: isDesktop ? "0" : "24px 0",
        }}
      >
        <TextField
          fullWidth={!isDesktop}
          placeholder={intl.formatMessage({ id: "recruiter.kanban.search_candidates" })}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <img src={SearchIcon} alt="search" style={{ width: 24, height: 24 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            backgroundColor: "#FFF",
            borderRadius: "999px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "999px",
              border: "none",
              height: "44px",
              width: "310px",
              paddingRight: isDesktop ? "8px" : "14px",
            },
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
          }}
        />

        <Box
          sx={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexWrap: isDesktop ? "wrap" : "nowrap",
            justifyContent: isDesktop ? "flex-start" : "space-between",
          }}
        >
          <Button
            variant="outlined"
            onClick={(e) => {
              const el = e?.currentTarget || null;
              setManageAnchorEl(el);
              // Track outside the updater: React may run updaters twice (StrictMode).
              if (!manageOpen) trackEvent("kanban", "manage_board_opened");
              setManageOpen((prev) => !prev);
            }}
            sx={{
              borderRadius: "999px",
              height: "44px",
              color: `${theme.palette.text.main} !important`,
              border: `1px solid ${theme.palette.secondary.grey_strong} !important`,
              backgroundColor: "#F9FAFF",
              textTransform: "none",
              padding: "0 22px",
              width: "auto",
              flex: isDesktop ? "0 0 auto" : "1 1 0",
              fontWeight: "800 !important",
              fontFamily: "Segoe UI Bold, sans-serif",
              "&:hover": { backgroundColor: theme.palette.secondary.hover, borderColor: theme.palette.divider },
            }}
          >
            <FormattedMessage id="recruiter.kanban.manage_board" />
          </Button>
          <Button
            variant="contained"
            onClick={() => setAddModalOpen(true)}
            sx={{
              borderRadius: "999px",
              height: "44px",
              backgroundColor: `${theme.palette.text.main} !important`,
              color: "#F1F1F1 !important",
              textTransform: "none",
              fontWeight: "800 !important",
              fontFamily: "Segoe UI Bold, sans-serif",
              padding: "0 22px",
              width: "auto",
              flex: isDesktop ? "0 0 auto" : "1 1 0",
              boxShadow: "none !important",
              "&:hover": { backgroundColor: `${theme.palette.secondary.hover} !important`, color: `${theme.palette.text.main} !important` },

            }}
          >
            <FormattedMessage id="recruiter.kanban.add_candidate" />
          </Button>
        </Box>
      </Box>

      <ExternalCandidateModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => {
          if (typeof onAddCandidate === "function") onAddCandidate();
        }}
      />

      {!isDesktop ? (
        <Modal
          open={manageOpen}
          onClose={() => {
            setManageOpen(false);
            setManageAnchorEl(null);
          }}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "calc(100vw - 32px)",
              maxWidth: "420px",
              outline: "none",
            }}
          >
            <ManageBoardPanel
              columns={columns}
              cardsByColumnId={cardsByColumnId}
              setColumns={setColumns}
              setCardsByColumnId={setCardsByColumnId}
              onBoardMutated={onBoardMutated}
            />
          </Box>
        </Modal>
      ) : (
        <Popper
          open={manageOpen && Boolean(manageAnchorEl)}
          anchorEl={manageAnchorEl}
          placement="bottom-end"
          sx={{ zIndex: 1000 }}
          modifiers={[
            { name: "offset", options: { offset: [0, 1] } },
            { name: "preventOverflow", options: { padding: 8 } },
            { name: "flip", enabled: false },
          ]}
        >
          <ClickAwayListener
            onClickAway={() => {
              setManageOpen(false);
              setManageAnchorEl(null);
            }}
          >
            <Box>
              <ManageBoardPanel
                columns={columns}
                cardsByColumnId={cardsByColumnId}
                setColumns={setColumns}
                setCardsByColumnId={setCardsByColumnId}
                onBoardMutated={onBoardMutated}
              />
            </Box>
          </ClickAwayListener>
        </Popper>
      )}
    </Box>
  );
};

export default HeaderRowKanban;


